import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { MailCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/misc';
import { AuthLayout } from '@/components/AuthLayout';
import { Turnstile } from '@/components/Turnstile';
import { PhoneInput } from '@/components/PhoneInput';
import { PasswordInput } from '@/components/PasswordInput';
import { DEFAULT_COUNTRY } from '@/lib/phone';
import { makeClient } from '@/lib/api';
import { track } from '@/lib/analytics';
import { trackCompleteRegistration } from '@/lib/metaPixel';
import { suggestEmail } from '@/lib/email';
import { useT } from '@/lib/i18n';

export function Signup() {
  const t = useT();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [locale, setLocale] = useState(DEFAULT_COUNTRY.locale);
  const [company, setCompany] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [token, setToken] = useState<string | null>(null);
  const [captchaKey, setCaptchaKey] = useState(0); // bump → Turnstile gera um token fresco
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const emailHint = useMemo(() => suggestEmail(email), [email]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 8) { toast.error(t('auth.passwordTooShort')); return; }
    if (password !== confirm) { toast.error(t('auth.passwordMismatch')); return; }
    // Sem token do captcha o servidor devolveria 403 — evita o envio fadado e força o
    // widget a (re)gerar um token fresco (cobre expiração no formulário longo e adblock).
    if (!token) { toast.error(t('auth.captchaIncomplete')); setCaptchaKey((k) => k + 1); return; }
    setBusy(true);
    try {
      const { error, response } = await makeClient().POST('/auth/signup', {
        body: { email, password, name, phone, company, job_title: jobTitle, locale, turnstile_token: token ?? undefined },
      });
      if (error) {
        const code = (error as { code?: string } | undefined)?.code;
        toast.error(
          response?.status === 409 ? t('auth.emailTaken')
          : code === 'captcha_failed' || code === 'captcha_unavailable' ? t('auth.captchaFailed')
          : t('auth.signupFailed'),
        );
        // Token do captcha é de uso único: renova p/ a próxima tentativa.
        setToken(null);
        setCaptchaKey((k) => k + 1);
        return;
      }
      track('sign_up', { method: 'password' });
      trackCompleteRegistration();
      setDone(true);
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <AuthLayout subtitle={t('auth.almostThere')}>
        <div className="rounded-xl border bg-card p-8 text-center shadow-sm">
          <div className="mx-auto mb-4 flex size-14 items-center justify-center rounded-full bg-primary/12">
            <MailCheck className="size-7 text-primary" />
          </div>
          <h2 className="text-xl font-bold">{t('auth.checkEmailTitle')}</h2>
          <p className="mt-2 text-sm text-muted-foreground">{t('auth.checkEmailBody', { email })}</p>
          <Button asChild className="mt-6 w-full"><Link to="/login">{t('auth.goToLogin')}</Link></Button>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout subtitle={t('auth.signupSubtitle')}>
      <div className="rounded-xl border bg-card p-6 shadow-sm">
        <form onSubmit={submit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="name">{t('auth.name')}</Label>
            <Input id="name" required value={name} onChange={(e) => setName(e.target.value)} placeholder={t('auth.namePlaceholder')} />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="email">{t('auth.email')}</Label>
            <Input id="email" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="voce@empresa.com" />
            {emailHint && (
              <button
                type="button"
                onClick={() => setEmail(emailHint)}
                className="self-start text-xs font-medium text-amber-700 hover:underline"
              >
                {t('auth.emailSuggestion', { email: emailHint })}
              </button>
            )}
          </div>
          <div className="flex flex-col gap-2">
            <Label>{t('auth.phone')}</Label>
            <PhoneInput onChange={setPhone} onLocale={setLocale} />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="password">{t('auth.password')}</Label>
            <PasswordInput id="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} placeholder={t('auth.minChars')} />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="confirm">{t('auth.confirmPassword')}</Label>
            <PasswordInput id="confirm" required value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder={t('auth.confirmPlaceholder')} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-2">
              <Label htmlFor="company" className="text-muted-foreground">{t('auth.company')} <span className="font-normal">{t('auth.optional')}</span></Label>
              <Input id="company" value={company} onChange={(e) => setCompany(e.target.value)} placeholder={t('auth.companyPlaceholder')} />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="job" className="text-muted-foreground">{t('auth.jobTitle')} <span className="font-normal">{t('auth.optional')}</span></Label>
              <Input id="job" value={jobTitle} onChange={(e) => setJobTitle(e.target.value)} placeholder={t('auth.jobPlaceholder')} />
            </div>
          </div>

          <Turnstile onToken={setToken} resetKey={captchaKey} />
          <Button type="submit" disabled={busy} className="w-full">
            {busy && <Spinner className="border-primary-foreground/40 border-t-primary-foreground" />}
            {t('auth.createAccount')}
          </Button>
        </form>
      </div>

      <Link
        to="/login"
        className="mt-4 block w-full text-center text-sm text-muted-foreground transition-colors hover:text-foreground"
      >
        {t('auth.haveAccount')} <span className="font-semibold text-primary">{t('auth.enter')}</span>
      </Link>
    </AuthLayout>
  );
}
